import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UtensilsCrossed, Calendar, Users, MapPin, Info, Check, Star, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth, API } from '@/App';
import { toast } from 'sonner';

// Catering services data from Shopify
const CATERING_SERVICES = [
  {
    id: 'baby-shower',
    name: 'Baby Shower Afternoon Tea',
    shortName: 'Baby Shower',
    pricePerHead: 26,
    minGuests: 25,
    description: 'Mark this milestone with our Baby Shower Afternoon Tea—a thoughtful celebration of new beginnings designed for parents-to-be and their loved ones.',
    fullDescription: `Mark this milestone with our Baby Shower Afternoon Tea—a thoughtful celebration of new beginnings designed for parents-to-be and their loved ones.

Our curated menu features artisan sandwiches, freshly baked scones, and indulgent sweet treats crafted by our pastry chefs, with dietary options including vegan, gluten-free, and dairy-free choices.

Every element is handled: five hours of professional service, premium tableware, printed menus, hot and cold beverages, elegant styling, and a welcome glass of crémant.

Our style team personalises your space to perfection. Setup, service, and breakdown are fully managed within our 60-minute travel radius across London and the M25.`,
    images: [
      'https://www.cakerywonderland.com/cdn/shop/files/baby-shower-afternoon-tea-event-catering-london-m25afternoon-teacakery-wonderland-5444514.png?v=1771120531&width=800',
      'https://www.cakerywonderland.com/cdn/shop/files/baby-shower-afternoon-tea-event-catering-london-m25afternoon-teacakery-wonderland-6909340.png?v=1771120531&width=800',
      'https://www.cakerywonderland.com/cdn/shop/files/baby-shower-afternoon-tea-event-catering-london-m25afternoon-teacakery-wonderland-6024875.png?v=1771120532&width=800'
    ],
    serviceTypes: [
      { id: 'catered', name: 'Fully Catered', minGuests: 25 },
      { id: 'delivery', name: 'Delivery Only', minGuests: 25 }
    ],
    menu: {
      sandwiches: ['Spinach, roasted pepper & mature cheddar muffins', 'Mushroom, truffle and chive tarts', 'Panko coated chicken, avocado and tomato chutney wrap', 'Hummus, rocket, falafel tomato chutney wrap', 'Salmon, lemon & dill mayo on granary'],
      scones: ['Pink berry scones', 'Blueberry scones', 'Served with lemon curd, raspberry jam & whipped clotted cream'],
      sweets: ['Summer macarons', 'Golden truffle brownies', 'White chocolate brownies', 'Orange chocolate & vanilla shortbread teddies', 'Raspberry pink gin drizzle cakes', 'Mini banana & caramel meringue tarts'],
      drinks: ['Freshly brewed single roast coffee', 'Royal blend afternoon tea', 'Freshly made peach ice tea', 'Still and sparkling water']
    },
    minSpend: { weekday: 1000, weekend: 1500 },
    vatApplies: true
  },
  {
    id: 'celebration',
    name: 'Celebration Afternoon Tea',
    shortName: 'Celebration',
    pricePerHead: 18.50,
    minGuests: 25,
    description: 'Perfect for birthdays, anniversaries, and special occasions. A classic afternoon tea experience with our signature touches.',
    fullDescription: `Celebrate life's special moments with our Celebration Afternoon Tea—perfect for birthdays, anniversaries, hen parties, and any occasion worth marking.

Our expert team delivers a complete experience with artisan finger sandwiches, freshly baked scones with clotted cream and preserves, and a stunning selection of sweet treats.

Available as fully catered with our professional service team, or delivery-only for a more intimate gathering. Dietary requirements happily accommodated.`,
    images: [
      'https://www.cakerywonderland.com/cdn/shop/files/celebration-afternoon-tea-event-catering-i-delivery-in-londonafternoon-teacakery-wonderland-7856980.png?v=1771120528&width=800',
      'https://www.cakerywonderland.com/cdn/shop/files/celebration-afternoon-tea-event-catering-i-delivery-in-londonafternoon-teacakery-wonderland-4944301.png?v=1771120530&width=800'
    ],
    serviceTypes: [
      { id: 'catered', name: 'Fully Catered', minGuests: 25 },
      { id: 'delivery', name: 'Delivery Only', minGuests: 10 }
    ],
    menu: {
      sandwiches: ['Selection of 5 artisan finger sandwiches', 'Vegetarian options available', 'Vegan and gluten-free on request'],
      scones: ['Freshly baked fruit scones', 'Plain scones', 'Clotted cream and preserves'],
      sweets: ['Selection of mini cakes', 'Macarons', 'Chocolate truffles', 'Seasonal tarts'],
      drinks: ['Tea selection', 'Coffee', 'Soft drinks']
    },
    minSpend: { weekday: 500, weekend: 750 },
    vatApplies: true
  },
  {
    id: 'deluxe',
    name: 'Deluxe Afternoon Tea',
    shortName: 'Deluxe',
    pricePerHead: 23,
    minGuests: 25,
    description: 'An elevated afternoon tea experience with premium ingredients and champagne service.',
    fullDescription: `Indulge in our Deluxe Afternoon Tea—an elevated experience featuring premium ingredients and exquisite presentation.

Includes a glass of champagne or Prosecco on arrival, followed by our signature three-tier afternoon tea with artisan sandwiches, warm scones, and an array of pastry chef-crafted desserts.

Perfect for milestone celebrations, corporate entertaining, or any occasion deserving of something extra special.`,
    images: [
      'https://www.cakerywonderland.com/cdn/shop/files/deluxe-afternoon-tea-event-catering-i-delivery-in-londonafternoon-teacakery-wonderland-3280978.png?v=1771120532&width=800',
      'https://www.cakerywonderland.com/cdn/shop/files/deluxe-afternoon-tea-event-catering-i-delivery-in-londonafternoon-teacakery-wonderland-1006070.png?v=1771120535&width=800'
    ],
    serviceTypes: [
      { id: 'catered', name: 'Fully Catered', minGuests: 25 },
      { id: 'delivery', name: 'Delivery Only', minGuests: 15 }
    ],
    menu: {
      sandwiches: ['Smoked salmon & cream cheese', 'Coronation chicken', 'Cucumber & cream cheese', 'Egg mayonnaise & cress', 'Beef & horseradish'],
      scones: ['Plain and fruit scones', 'Clotted cream', 'Strawberry preserve', 'Lemon curd'],
      sweets: ['Champagne macarons', 'Victoria sponge fingers', 'Chocolate éclairs', 'Lemon tarts', 'Raspberry cheesecake bites'],
      drinks: ['Glass of Champagne or Prosecco', 'Premium tea selection', 'Artisan coffee', 'Soft drinks']
    },
    minSpend: { weekday: 750, weekend: 1000 },
    vatApplies: true
  },
  {
    id: 'cakery',
    name: 'The Cakery Afternoon Tea',
    shortName: 'Cakery Classic',
    pricePerHead: 17,
    minGuests: 25,
    description: 'Our signature afternoon tea experience featuring the best of Cakery Wonderland.',
    fullDescription: `Experience the essence of Cakery Wonderland with our signature afternoon tea—a celebration of British baking tradition with our unique artisan twist.

Featuring our award-winning scones, handcrafted sandwiches, and a stunning array of cakes and pastries that showcase our pastry chefs' creativity.

Available for events across London and the M25. 15% of all proceeds supports food poverty charities.`,
    images: [
      'https://www.cakerywonderland.com/cdn/shop/files/the-cakery-afternoon-tea-event-catering-i-delivery-in-londonafternoon-teacakery-wonderland-1937443.png?v=1771120533&width=800',
      'https://www.cakerywonderland.com/cdn/shop/files/the-cakery-afternoon-tea-event-catering-i-delivery-in-londonafternoon-teacakery-wonderland-8192535.png?v=1771120536&width=800'
    ],
    serviceTypes: [
      { id: 'catered', name: 'Fully Catered', minGuests: 25 },
      { id: 'delivery', name: 'Delivery Only', minGuests: 10 }
    ],
    menu: {
      sandwiches: ['Classic finger sandwiches', 'Seasonal fillings', 'Vegetarian options included'],
      scones: ['Award-winning fruit scones', 'Clotted cream & jam'],
      sweets: ['Mini Victoria sponge', 'Lemon drizzle', 'Brownies', 'Seasonal macarons'],
      drinks: ['Tea', 'Coffee', 'Soft drinks']
    },
    minSpend: { weekday: 500, weekend: 650 },
    vatApplies: true
  },
  {
    id: 'alice-wonderland',
    name: 'Alice in Wonderland Experience',
    shortName: 'Alice in Wonderland',
    pricePerHead: null,
    fixedPrice: 2500,
    minGuests: 20,
    description: 'An immersive themed afternoon tea experience straight from Wonderland.',
    fullDescription: `Fall down the rabbit hole with our immersive Alice in Wonderland Afternoon Tea Experience—a theatrical dining adventure like no other.

Transform your venue into Wonderland with our complete styling package including themed décor, character performers, and interactive elements that bring the story to life.

Our Wonderland menu features whimsical treats, "Drink Me" potions, "Eat Me" cakes, and surprises around every corner. Perfect for landmark birthdays, hen parties, and anyone who believes in impossible things.

Price includes full venue styling, catering for up to 30 guests, entertainment, and all props and décor.`,
    images: [
      'https://www.cakerywonderland.com/cdn/shop/files/immersive-alice-in-wonderland-afternoon-tea-experienceafternoon-teacakery-wonderland-7348357.jpg?v=1770338757&width=800',
      'https://www.cakerywonderland.com/cdn/shop/files/immersive-alice-in-wonderland-afternoon-tea-experienceafternoon-teacakery-wonderland-9933386.png?v=1770338757&width=800'
    ],
    serviceTypes: [
      { id: 'full-experience', name: 'Full Immersive Experience', minGuests: 20 }
    ],
    menu: {
      sandwiches: ['Themed finger sandwiches', '"Eat Me" labeled treats'],
      scones: ['Queen of Hearts scones', 'Red and white themed'],
      sweets: ['"Drink Me" potion bottles', 'Cheshire Cat macarons', 'Mad Hatter cupcakes', 'Playing card cookies'],
      drinks: ['Themed "potions"', 'Tea served in mismatched china', 'Wonderland cocktails (optional)']
    },
    minSpend: null,
    vatApplies: true
  },
  {
    id: 'bridgerton',
    name: 'Bridgerton Afternoon Tea Experience',
    shortName: 'Bridgerton',
    pricePerHead: null,
    fixedPrice: 1600,
    maxGuests: 8,
    minGuests: 6,
    description: 'A Regency-era inspired afternoon tea experience for intimate gatherings.',
    fullDescription: `Step into the elegant world of Bridgerton with our Regency-inspired Afternoon Tea Experience—perfect for intimate celebrations.

Designed for groups of 6-8, this exclusive experience includes Regency-era styling, period-appropriate entertainment, and a menu inspired by the lavish gatherings of the ton.

Includes venue styling, premium afternoon tea service, themed entertainment, and keepsake items for all guests.`,
    images: [
      'https://www.cakerywonderland.com/cdn/shop/files/lavish-mothers-day-bridgeton-afternoon-tea-experience-for-8afternoon-teacakery-wonderland-events-2579681.jpg?v=1770291916&width=800',
      'https://www.cakerywonderland.com/cdn/shop/files/lavish-mothers-day-bridgeton-afternoon-tea-experience-for-8afternoon-teacakery-wonderland-events-6986505.jpg?v=1770291916&width=800'
    ],
    serviceTypes: [
      { id: 'full-experience', name: 'Full Bridgerton Experience (6-8 guests)', minGuests: 6 }
    ],
    menu: {
      sandwiches: ['Regency-inspired finger sandwiches', 'Dainty canapés'],
      scones: ['Rose-scented scones', 'Lavender shortbread'],
      sweets: ['Lady Whistledown cakes', 'Diamond of the Season macarons', 'Featherington tartlets'],
      drinks: ['Ratafia', 'Lemonade', 'Fine teas', 'Optional champagne']
    },
    minSpend: null,
    vatApplies: true
  }
];

const DEPOSIT_AMOUNT = 200;

// Catering List Page Component
const CateringListPage = () => {
  return (
    <div className="min-h-screen bg-background pt-16" data-testid="catering-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden hero-gradient py-20">
        <div className="absolute top-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <UtensilsCrossed className="w-12 h-12 text-primary mx-auto mb-6 animate-float" />
            <h1 className="text-5xl font-heading font-bold text-white mb-4" data-testid="catering-title">
              Book Catering
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto font-body">
              From intimate gatherings to grand celebrations. Choose your perfect afternoon tea experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Deposit Info Banner */}
      <section className="py-4 bg-primary/10 border-y border-primary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-4 flex-wrap text-center">
            <Info className="w-5 h-5 text-primary" />
            <p className="text-white font-body">
              <span className="font-bold">£{DEPOSIT_AMOUNT} deposit</span> to secure your booking • Deducted from final balance • Remaining balance due 10 days before event
            </p>
          </div>
        </div>
      </section>

      {/* Catering Services Grid */}
      <section className="py-16 bg-background-elevated">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {CATERING_SERVICES.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={`/catering/${service.id}`} data-testid={`catering-card-${service.id}`}>
                  <div className="bg-background-paper rounded-2xl overflow-hidden border border-primary/20 hover:border-primary/50 transition-all group h-full">
                    <div className="aspect-[4/3] overflow-hidden relative">
                      <img
                        src={service.images[0]}
                        alt={service.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-sm font-bold">
                        {service.pricePerHead ? `From £${service.pricePerHead}/head` : `From £${service.fixedPrice}`}
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-heading font-bold text-white mb-2">{service.name}</h3>
                      <p className="text-text-secondary font-body text-sm mb-4 line-clamp-2">{service.description}</p>
                      <div className="flex items-center gap-4 text-sm text-text-muted">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4 text-primary" />
                          Min {service.minGuests} guests
                        </span>
                      </div>
                      <Button className="w-full mt-4 bg-primary hover:bg-primary-hover text-white rounded-full">
                        View Details & Book
                      </Button>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-heading font-bold text-white mb-8">Why Choose Our Catering?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-background-paper rounded-xl p-6 border border-primary/20">
              <Star className="w-10 h-10 text-primary mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold text-white mb-2">Award Winning</h3>
              <p className="text-text-secondary text-sm">UK's most sustainable caterer three years running</p>
            </div>
            <div className="bg-background-paper rounded-xl p-6 border border-primary/20">
              <UtensilsCrossed className="w-10 h-10 text-primary mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold text-white mb-2">Bespoke Menus</h3>
              <p className="text-text-secondary text-sm">Customised to your theme and dietary requirements</p>
            </div>
            <div className="bg-background-paper rounded-xl p-6 border border-primary/20">
              <MapPin className="w-10 h-10 text-primary mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold text-white mb-2">London & M25</h3>
              <p className="text-text-secondary text-sm">We cater across Greater London and beyond</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Catering Detail Page Component
const CateringDetailPage = ({ serviceId }) => {
  const service = CATERING_SERVICES.find(s => s.id === serviceId);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentImage, setCurrentImage] = useState(0);
  const [selectedService, setSelectedService] = useState(service?.serviceTypes[0]?.id || '');
  const [guestCount, setGuestCount] = useState(service?.minGuests || 25);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    eventDate: '',
    venue: '',
    additionalInfo: '',
    name: user?.name || '',
    email: user?.email || '',
    phone: ''
  });

  if (!service) {
    return (
      <div className="min-h-screen bg-background pt-16 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-white mb-4">Service not found</h2>
          <Link to="/catering">
            <Button className="bg-primary hover:bg-primary-hover text-white rounded-full">
              Back to Catering
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const selectedServiceType = service.serviceTypes.find(s => s.id === selectedService);
  const minGuests = selectedServiceType?.minGuests || service.minGuests;
  const maxGuests = service.maxGuests || 200;

  // Calculate total price
  const calculateTotal = () => {
    if (service.fixedPrice) {
      return service.fixedPrice;
    }
    return service.pricePerHead * guestCount;
  };

  const total = calculateTotal();
  const balanceDue = total - DEPOSIT_AMOUNT;

  // Get minimum date (14 days from now)
  const getMinDate = () => {
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 14);
    return minDate.toISOString().split('T')[0];
  };

  const handleProceedToCheckout = () => {
    if (!user) {
      toast.error('Please login to book catering');
      navigate('/login');
      return;
    }
    setShowBookingForm(true);
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    
    if (!formData.eventDate || !formData.name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/catering/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          service_id: service.id,
          service_name: service.name,
          service_type: selectedService,
          guest_count: guestCount,
          total_price: total,
          deposit_amount: DEPOSIT_AMOUNT,
          event_date: formData.eventDate,
          venue: formData.venue,
          additional_info: formData.additionalInfo,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create booking');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-16" data-testid={`catering-detail-${service.id}`}>
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <Link to="/catering" className="inline-flex items-center text-text-secondary hover:text-primary transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to all catering
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div>
            <div className="aspect-square rounded-2xl overflow-hidden mb-4">
              <img
                src={service.images[currentImage]}
                alt={service.name}
                className="w-full h-full object-cover"
              />
            </div>
            {service.images.length > 1 && (
              <div className="flex gap-2">
                {service.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImage(idx)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === currentImage ? 'border-primary' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div>
            <h1 className="text-4xl font-heading font-bold text-white mb-4">{service.name}</h1>
            
            <div className="text-3xl font-accent text-primary mb-6">
              {service.pricePerHead ? (
                <>From £{service.pricePerHead} <span className="text-lg text-text-secondary">per person</span></>
              ) : (
                <>£{service.fixedPrice?.toLocaleString()} <span className="text-lg text-text-secondary">total</span></>
              )}
            </div>

            {/* Service Type Selection */}
            <div className="mb-6">
              <label className="block text-white text-sm font-medium mb-2">Service Type</label>
              <div className="flex flex-wrap gap-2">
                {service.serviceTypes.map(type => (
                  <button
                    key={type.id}
                    onClick={() => {
                      setSelectedService(type.id);
                      if (guestCount < type.minGuests) setGuestCount(type.minGuests);
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedService === type.id
                        ? 'bg-primary text-white'
                        : 'bg-background-elevated text-white hover:bg-primary/20 border border-primary/30'
                    }`}
                    data-testid={`service-type-${type.id}`}
                  >
                    {type.name} (min {type.minGuests})
                  </button>
                ))}
              </div>
            </div>

            {/* Guest Count */}
            {service.pricePerHead && (
              <div className="mb-6">
                <label className="block text-white text-sm font-medium mb-2">
                  Number of Guests <span className="text-text-muted">(min {minGuests}{service.maxGuests ? `, max ${service.maxGuests}` : ''})</span>
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setGuestCount(Math.max(minGuests, guestCount - 5))}
                    className="w-10 h-10 rounded-full bg-background-elevated text-white hover:bg-primary/20 border border-primary/30"
                  >
                    -
                  </button>
                  <Input
                    type="number"
                    value={guestCount}
                    onChange={(e) => setGuestCount(Math.max(minGuests, Math.min(maxGuests, parseInt(e.target.value) || minGuests)))}
                    min={minGuests}
                    max={maxGuests}
                    className="w-24 text-center bg-background-elevated border-primary/30 text-white"
                    data-testid="guest-count-input"
                  />
                  <button
                    onClick={() => setGuestCount(Math.min(maxGuests, guestCount + 5))}
                    className="w-10 h-10 rounded-full bg-background-elevated text-white hover:bg-primary/20 border border-primary/30"
                  >
                    +
                  </button>
                </div>
              </div>
            )}

            {/* Price Summary */}
            <div className="bg-background-elevated rounded-xl p-6 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-text-secondary">Total</span>
                <span className="text-2xl font-bold text-white">£{total.toLocaleString()}</span>
              </div>
              {service.vatApplies && (
                <p className="text-text-muted text-xs mb-4">VAT applies to catered events</p>
              )}
              <div className="border-t border-white/10 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Deposit (today)</span>
                  <span className="text-primary font-bold">£{DEPOSIT_AMOUNT}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Balance due (10 days before)</span>
                  <span className="text-white">£{balanceDue.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <Button
              onClick={handleProceedToCheckout}
              className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-4 text-lg font-semibold shadow-lg shadow-primary/30 mb-4"
              data-testid="proceed-to-checkout-btn"
            >
              Pay £{DEPOSIT_AMOUNT} Deposit to Book
            </Button>

            <p className="text-text-muted text-xs text-center">
              Deposit is deducted from total. Remaining balance due 10 days before your event.
            </p>
          </div>
        </div>

        {/* Description & Menu */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-background-paper rounded-2xl p-8 border border-primary/20">
            <h2 className="text-2xl font-heading font-bold text-white mb-4">About This Experience</h2>
            <div className="text-text-secondary font-body whitespace-pre-line">{service.fullDescription}</div>
            
            {service.minSpend && (
              <div className="mt-6 p-4 bg-background-elevated rounded-xl">
                <p className="text-white font-semibold mb-2">Minimum Spend</p>
                <p className="text-text-secondary text-sm">Weekdays: £{service.minSpend.weekday.toLocaleString()}</p>
                <p className="text-text-secondary text-sm">Weekends: £{service.minSpend.weekend.toLocaleString()}</p>
              </div>
            )}
          </div>

          <div className="bg-background-paper rounded-2xl p-8 border border-primary/20">
            <h2 className="text-2xl font-heading font-bold text-white mb-4">What's Included</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-primary font-semibold mb-2">Sandwiches & Savouries</h3>
                <ul className="space-y-1">
                  {service.menu.sandwiches.map((item, i) => (
                    <li key={i} className="text-text-secondary text-sm flex items-start gap-2">
                      <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-primary font-semibold mb-2">Scones</h3>
                <ul className="space-y-1">
                  {service.menu.scones.map((item, i) => (
                    <li key={i} className="text-text-secondary text-sm flex items-start gap-2">
                      <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-primary font-semibold mb-2">Sweet Treats</h3>
                <ul className="space-y-1">
                  {service.menu.sweets.map((item, i) => (
                    <li key={i} className="text-text-secondary text-sm flex items-start gap-2">
                      <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-primary font-semibold mb-2">Drinks</h3>
                <ul className="space-y-1">
                  {service.menu.drinks.map((item, i) => (
                    <li key={i} className="text-text-secondary text-sm flex items-start gap-2">
                      <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Form Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="booking-form-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-background-paper rounded-2xl max-w-lg w-full p-8 border border-primary/20 my-8"
          >
            <h2 className="text-2xl font-heading font-bold text-white mb-2">Complete Your Booking</h2>
            <p className="text-text-secondary mb-6">{service.name} for {guestCount} guests</p>

            <form onSubmit={handleSubmitBooking} className="space-y-4">
              <div>
                <label className="block text-white text-sm font-medium mb-2">Event Date *</label>
                <Input
                  type="date"
                  value={formData.eventDate}
                  onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                  min={getMinDate()}
                  required
                  className="w-full bg-background-elevated border-primary/30 text-white rounded-lg"
                  data-testid="event-date-input"
                />
              </div>

              <div>
                <label className="block text-white text-sm font-medium mb-2">Venue / Location</label>
                <Input
                  type="text"
                  value={formData.venue}
                  onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                  placeholder="Enter venue address"
                  className="w-full bg-background-elevated border-primary/30 text-white rounded-lg"
                  data-testid="venue-input"
                />
              </div>

              <div>
                <label className="block text-white text-sm font-medium mb-2">Your Name *</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full bg-background-elevated border-primary/30 text-white rounded-lg"
                  data-testid="name-input"
                />
              </div>

              <div>
                <label className="block text-white text-sm font-medium mb-2">Email *</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full bg-background-elevated border-primary/30 text-white rounded-lg"
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="block text-white text-sm font-medium mb-2">Phone</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="07xxx xxxxxx"
                  className="w-full bg-background-elevated border-primary/30 text-white rounded-lg"
                  data-testid="phone-input"
                />
              </div>

              <div>
                <label className="block text-white text-sm font-medium mb-2">Special Requests / Dietary Requirements</label>
                <textarea
                  value={formData.additionalInfo}
                  onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                  placeholder="Any special requirements..."
                  rows={3}
                  className="w-full bg-background-elevated border border-primary/30 text-white rounded-lg px-4 py-3 resize-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  data-testid="additional-info-input"
                />
              </div>

              {/* Order Summary */}
              <div className="bg-background-elevated rounded-xl p-4">
                <h3 className="text-white font-semibold mb-3">Order Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{service.name}</span>
                    <span className="text-white">£{total.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{guestCount} guests</span>
                  </div>
                  <div className="border-t border-white/10 pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="text-primary font-semibold">Deposit to pay today</span>
                      <span className="text-primary font-bold">£{DEPOSIT_AMOUNT}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-text-muted">Balance due 10 days before</span>
                      <span className="text-text-secondary">£{balanceDue.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowBookingForm(false)}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10 rounded-full"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-primary hover:bg-primary-hover text-white rounded-full"
                  data-testid="confirm-booking-btn"
                >
                  {loading ? 'Processing...' : `Pay £${DEPOSIT_AMOUNT} Deposit`}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// Main Catering Page with Router
const CateringPage = () => {
  const { serviceId } = useParams();
  
  if (serviceId) {
    return <CateringDetailPage serviceId={serviceId} />;
  }
  
  return <CateringListPage />;
};

export default CateringPage;
export { CATERING_SERVICES, DEPOSIT_AMOUNT };
